/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import {
  Badge,
  ConfigProvider,
  Divider,
  Dropdown,
  Empty,
  Icon,
  Menu,
  message,
  Table,
  Tooltip,
} from 'antd';
import { formatRFC7231 } from 'date-fns';
import { useEffect, useState } from 'react';
import FloatingCard from '~components/FloatingCard';
import { getSystemHistory } from '~lib/netconnClient';
import constants from '~util/constants';
import handleAuthErr from '~util/handleAuthErr';
import logger from '~util/logger';
import CardTitle from './components/CardTitle';

const ConfigHistoryPane = ({ currentEventID, division, gridArea, nai = null }) => {
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const loadHistory = () => {
    if (!!nai) {
      setIsLoading(true);
      getSystemHistory(nai)
        .then(data => {
          setHistory(data);
          setIsLoading(false);
        })
        .catch(
          handleAuthErr(err => {
            setIsLoading(false);
            logger.error(err);
            message.error(constants.API_ERR_MESSAGE);
          })
        );
    }
  };

  useEffect(loadHistory, [nai, currentEventID]);

  const expandAll = () => {
    if (!!history?.map) setExpandedRowKeys(history.map(el => el.timestamp));
  };

  const collapseAll = () => {
    setExpandedRowKeys([]);
  };

  const dropdownMenu = () => (
    <Menu>
      <Menu.Item onClick={loadHistory}>
        <Icon type="reload" />
        Reload
      </Menu.Item>
      <Menu.Item onClick={expandAll} disabled={!history?.length}>
        <Icon type="plus-circle" />
        Expand All
      </Menu.Item>
      <Menu.Item onClick={collapseAll} disabled={!history?.length}>
        <Icon type="minus-circle" />
        Collapse All
      </Menu.Item>
    </Menu>
  );

  const tableColumnsBA = [
    datetimeColumn,
    statusColumn,
    naiColumn,
    subnetGroupColumn,
    actionColumn,
    userColumn,
  ];
  const tableColumnsCA = [
    datetimeColumn,
    statusColumn,
    airlineCodeColumn,
    naiColumn,
    systemModelColumn,
    actionColumn,
    userColumn,
  ];

  const cssRootContainer = css`
    border-color: #1976d2ab;
    border-style: solid;
    border-width: 2px 0 0 0;
    display: flex;
    flex-direction: column;
    grid-area: ${gridArea};
    height: auto;
    width: auto;
  `;

  const cssHeader = css`
    align-items: center;
    display: flex;
    justify-content: space-between;
  `;

  const cssTitleGroup = css`
    align-items: center;
    display: flex;
  `;

  return (
    <FloatingCard css={cssRootContainer} data-testid="history">
      <div css={cssHeader}>
        <div css={cssTitleGroup}>
          <CardTitle>History</CardTitle>
          <Tooltip title='Click on a row to expand the aircard details, or select "Expand All" from the menu to the right.'>
            <Icon
              type="question-circle"
              style={{ color: '#757575', marginLeft: '0.4rem', fontSize: '0.9rem' }}
            />
          </Tooltip>
        </div>
        <Dropdown
          disabled={!nai}
          overlay={dropdownMenu}
          placement="bottomRight"
          trigger={['click']}
        >
          <Icon type="ellipsis" style={{ fontSize: '1.4rem' }} />
        </Dropdown>
      </div>
      <Divider style={{ margin: '0.6rem 0' }} />
      <ConfigProvider renderEmpty={customEmpty}>
        <Table
          columns={division === constants.BA ? tableColumnsBA : tableColumnsCA}
          dataSource={history}
          expandIcon={customExpandIcon}
          expandedRowKeys={expandedRowKeys}
          expandedRowRender={nestedTable}
          expandRowByClick
          loading={isLoading}
          onExpandedRowsChange={setExpandedRowKeys}
          pagination={false}
          rowKey="timestamp"
          scroll={{ y: 260 }}
          size="middle"
        />
      </ConfigProvider>
    </FloatingCard>
  );
};

// =======================================
// ============ Table Columns ============
// =======================================

const datetimeColumn = {
  title: 'Date/Time',
  dataIndex: 'timestamp',
  sorter: (a, b) => {
    return new Date(a.timestamp) - new Date(b.timestamp);
  },
  defaultSortOrder: 'descend',
  sortDirections: ['descend', 'ascend'],
  render: timestamp => formatRFC7231(new Date(timestamp)),
};

const statusColumn = {
  title: 'Status',
  dataIndex: 'active',
  render: (active) => {
    return <Badge
      status={!!active ? 'success' : 'error'}
      text={!!active ? 'Active' : 'Inactive'}
      title={!!active ? 'Active' : 'Inactive'}
    />
  },
};

const airlineCodeColumn = {
  title: 'Airline Code',
  dataIndex: 'airlineCode',
};

const systemModelColumn = {
  title: 'System Model',
  dataIndex: 'model',
};

const naiColumn = {
  title: 'NAI',
  dataIndex: 'nai',
};

const subnetGroupColumn = {
  title: 'Subnet Group',
  dataIndex: 'subnetGroup',
};

const actionColumn = {
  title: 'Action',
  dataIndex: 'action',
  render: action => action.charAt(0).toUpperCase() + action.slice(1).toLowerCase(),
};

const userColumn = {
  title: 'User',
  dataIndex: 'username',
};

const nestedTable = record => {
  const nestedColumns = [
    { key: 'aircardIndex', render: (_text, _record, index) => `${index + 1}`, width: 30 },
    { title: 'IP Address', dataIndex: 'ip' },
    { title: 'IMSI', dataIndex: 'imsi' },
    { title: 'ESN', dataIndex: 'esn' },
    { title: 'Type', dataIndex: 'type' },
  ];

  return (
    <Table
      columns={nestedColumns}
      dataSource={record.aircards}
      pagination={false}
      rowKey="id"
      size="small"
    />
  );
};

const customExpandIcon = ({ expanded }) => {
  return (
    <Tooltip title="Aircard details">
      <Icon type={expanded ? 'minus-circle' : 'plus-circle'} style={{ cursor: 'pointer' }} />
    </Tooltip>
  );
};

const customEmpty = () => {
  return <Empty />;
};

export default ConfigHistoryPane;
